import { useMemo, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { trackCouponInsertedEvent } from '@services/ecommerceService';

import { FATAL_ERROR_VOUCHER_CODE } from '../constants';

type TUseVoucherValidation = (params: IUseVoucherValidationProps) => {
  validationCallback: (voucherCode: string) => void;
  loading: boolean;
  validationErrors: string;
  validationSuccessful: boolean;
};

interface IUseVoucherValidationProps {
  serverValidationAction: (voucherCode: string) => Promise<string[]>;
  baseVoucherError: MessageDescriptor;
}

export const useVoucherValidation: TUseVoucherValidation = ({ serverValidationAction, baseVoucherError }) => {
  const { formatMessage } = useIntl();
  const [voucherValidation, setVoucherValidation] = useState('');
  const [loading, setLoading] = useState(false);

  return useMemo(() => {
    const validationCallback = async (untrimedVoucherCode: string) => {
      const voucherCode = untrimedVoucherCode.trim();
      if (!voucherCode) {
        setVoucherValidation(formatMessage(baseVoucherError));
        return;
      }

      setLoading(true);
      const result = await serverValidationAction(voucherCode);
      setLoading(false);

      // if there is no error message in result we can skip rest of the error building process
      if (result.length === 0) {
        setVoucherValidation('');
        trackCouponInsertedEvent({ code: voucherCode, status: true });
        return;
      }

      const errorMessage = result.includes(FATAL_ERROR_VOUCHER_CODE)
        ? formatMessage(baseVoucherError)
        : result.join(' ');
      setVoucherValidation(errorMessage);
      trackCouponInsertedEvent({ error: errorMessage, code: voucherCode, status: false });
    };

    return {
      loading,
      validationCallback,
      validationErrors: voucherValidation,
      validationSuccessful: voucherValidation.length === 0,
    };
  }, [
    loading,
    voucherValidation,
    setVoucherValidation,
    serverValidationAction,
    formatMessage,
    setLoading,
    baseVoucherError,
  ]);
};
